import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridHeaderButton } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddStatusUpdateOptionModal } from '@/components/modals/AddStatusUpdateOptionModal';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Dropdownlist } from '@/components/Dropdownlist';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { hasValue, getOfficerLabel, getOfficerPluralLabel } from '@/utils/legacy-utils';
import moment from 'moment';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

const tabs = [
  { id: 'showSettingsTab', label: 'Settings' },
  { id: 'showHistoryTab', label: 'History' },
];

const statusUpdateOptionColumns: KstDataGridColumn[] = [
  { field: 'id', name: 'Id', size: 'hidden' },
  {
    field: 'name',
    name: 'Name',
    size: 'col-xs-10',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.name)) {
        return e.name;
      }
      return 'Error getting name.';
    },
  },
];

const headerButtons: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

// historyColumns defined inside component via useMemo (needs dynamic officer label)

export function StatusUpdateOptionManagementScreen() {
  const { theme } = useTheme();

  const officerLabel = useMemo(() => getOfficerLabel(), []);
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  const historyColumns: KstDataGridColumn[] = useMemo(() => [
    { field: 'officerName', name: officerLabel },
    {
      field: 'updatedByName',
      name: 'Updated By',
      template: (e: any) => e.updatedByName || '',
    },
    {
      field: 'statusComment',
      name: 'Status',
      template: (e: any) => e.statusComment || '<span style="color:#999">Cleared</span>',
    },
    {
      field: 'latitude',
      name: '',
      width: '40px',
      template: (e: any) => {
        if (e.latitude != null && e.longitude != null) {
          return `<a href="https://www.google.com/maps?q=${e.latitude},${e.longitude}" target="_blank" rel="noopener" title="${e.latitude}, ${e.longitude}" style="color:#4a90e2"><i class="fas fa-map-marker-alt"></i></a>`;
        }
        return '';
      },
    },
    {
      field: 'dateCreated',
      name: 'Date',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.dateCreated)) {
          return moment(e.dateCreated).format('MM/DD/YYYY HH:mm:ss');
        }
        return '';
      },
    },
  ], [officerLabel]);

  const [activeTab, setActiveTab] = useState('showSettingsTab');

  const [entries, setEntries] = useState<any[] | null>(null);
  const listCacheRef = useRef<any>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [isStatusUpdateEnabled, setIsStatusUpdateEnabled] = useState(false);
  const [isStatusUpdateOnTourStopsEnabled, setIsStatusUpdateOnTourStopsEnabled] = useState(false);
  const [isStatusUpdateForDispatchersEnabled, setIsStatusUpdateForDispatchersEnabled] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // History state
  const [historyEntries, setHistoryEntries] = useState<any[] | null>(null);
  const historyLoadedRef = useRef(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyFromDate, setHistoryFromDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [historyToDate, setHistoryToDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [historyOfficerFilter, setHistoryOfficerFilter] = useState<number | null>(null);
  const [historyOfficerOptions, setHistoryOfficerOptions] = useState<{ id: number | null; name: string }[]>([]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<{ id: number; name: string } | null>(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  const gridContainerRef = useRef<HTMLDivElement>(null);

  const rows: KstDataGridRows = useMemo(
    () => ({ entries }),
    [entries]
  );

  const clearCachedValues = useCallback(() => {
    listCacheRef.current = null;
  }, []);

  const refreshList = useCallback((doScrollToBottom = false) => {
    setIsLoading(true);
    listCacheRef.current = null;
    api.listStatusUpdateOptions((result: any) => {
      const options = result.statusUpdateOptions || [];
      options.sort((a: any, b: any) => a.listIdx - b.listIdx);
      for (let i = 0; i < options.length; i++) {
        options[i].listIdx = i + 1;
        options[i].isListEnabled = true;
      }
      setEntries(options);
      setIsStatusUpdateEnabled(result.isStatusUpdateEnabled || false);
      setIsStatusUpdateOnTourStopsEnabled(result.isStatusUpdateOnTourStopsEnabled || false);
      setIsStatusUpdateForDispatchersEnabled(result.isStatusUpdateForDispatchersEnabled || false);
      setSettingsLoaded(true);
      setIsLoading(false);

      if (doScrollToBottom) {
        setTimeout(() => {
          const scrollEl = gridContainerRef.current?.closest('.kst-main-content');
          if (scrollEl) {
            scrollEl.scrollTop = scrollEl.scrollHeight;
          }
        }, 0);
      }
    }, () => {
      setIsLoading(false);
      App.showPopupNotification('Error getting status update options.', 'error');
    });
  }, []);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);

  const handleButtonCommand = useCallback((functionName: string, checkedItems: any[]) => {
    if (functionName === 'add') {
      setEditItem(null);
      setAddModalOpen(true);
    } else if (functionName === 'edit') {
      if (checkedItems.length === 0) {
        App.showPopupNotification('A status update option must be selected to edit.', 'warning');
        return;
      }
      if (checkedItems.length > 1) {
        App.showPopupNotification('Only one status update option can be edited at a time.', 'warning');
        return;
      }
      setEditItem({ id: checkedItems[0].id, name: checkedItems[0].name });
      setAddModalOpen(true);
    } else if (functionName === 'delete') {
      if (checkedItems.length === 0) {
        App.showPopupNotification('Status update option must be selected to delete.', 'warning');
        return;
      }
      const count = checkedItems.length;
      const message = count === 1
        ? 'Are you sure you want to delete this status update option?'
        : `Are you sure you want to delete ${count} status update options?`;
      setDeleteConfirmItems(checkedItems);
      setDeleteConfirmMessage(message);
      setDeleteConfirmOpen(true);
    }
  }, []);

  const handleAddSave = useCallback((data: { id?: number; name: string }) => {
    setIsSaving(true);
    if (data.id) {
      api.updateStatusUpdateOption({ id: data.id, name: data.name }, () => {
        setIsSaving(false);
        clearCachedValues();
        refreshList();
        setAddModalOpen(false);
        setEditItem(null);
      }, () => {
        setIsSaving(false);
        App.showPopupNotification('Failed to update status update option.', 'error');
      });
    } else {
      api.saveStatusUpdateOption({ name: data.name }, () => {
        setIsSaving(false);
        clearCachedValues();
        refreshList(true);
        setAddModalOpen(false);
        setEditItem(null);
      }, () => {
        setIsSaving(false);
        App.showPopupNotification('Failed to save status update option.', 'error');
      });
    }
  }, [clearCachedValues, refreshList]);

  const handleRowClick = useCallback((row: any) => {
    setEditItem({ id: row.id, name: row.name });
    setAddModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    const idList = deleteConfirmItems.map((elt: any) => elt.id);
    setIsDeleting(true);
    api.deleteStatusUpdateOptions({ idList }, () => {
      setIsDeleting(false);
      clearCachedValues();
      refreshList();
      setDeleteConfirmOpen(false);
      setDeleteConfirmItems([]);
      setDeleteConfirmMessage('');
    }, () => {
      setIsDeleting(false);
      App.showPopupNotification('Failed to delete status update option.', 'error');
    });
  }, [deleteConfirmItems, clearCachedValues, refreshList]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  const handleRowMoved = useCallback((row: any) => {
    setEntries((prev) => {
      const values = [...(prev ?? [])];
      const idxOfRow = values.findIndex((v: any) => v.id === row.id);
      if (idxOfRow >= 0) {
        values.splice(idxOfRow, 1);
      }
      values.splice(row.listIdx - 1, 0, row);
      for (let i = 0; i < values.length; i++) {
        values[i].listIdx = i + 1;
      }

      const idList = values.map((v: any) => v.id);
      api.reorderStatusUpdateOptions({ idList }, () => {
        clearCachedValues();
      }, () => {
        App.showPopupNotification('Failed to reorder status update options.', 'error');
      });

      return values;
    });
  }, [clearCachedValues]);

  const handleStatusSwitch = useCallback((id: number, isEnabled: boolean) => {
    setIsLoading(true);
    api.setStatusUpdateOptionEnabled({ id, isEnabled }, () => {
      clearCachedValues();
      setEntries((prev) => {
        if (!prev) return prev;
        return prev.map((entry) =>
          entry.id === id ? { ...entry, isEnabled } : entry
        );
      });
      setIsLoading(false);
    }, () => {
      App.showPopupNotification('Failed to update status.', 'error');
      setIsLoading(false);
    });
  }, [clearCachedValues]);

  const handleSettingChange = useCallback((field: string, value: boolean) => {
    if (isSavingSettings) return;
    setIsSavingSettings(true);
    const settings = {
      isStatusUpdateEnabled: field === 'isStatusUpdateEnabled' ? value : isStatusUpdateEnabled,
      isStatusUpdateOnTourStopsEnabled: field === 'isStatusUpdateOnTourStopsEnabled' ? value : isStatusUpdateOnTourStopsEnabled,
      isStatusUpdateForDispatchersEnabled: field === 'isStatusUpdateForDispatchersEnabled' ? value : isStatusUpdateForDispatchersEnabled,
    };
    api.updateStatusUpdateSettings(settings, () => {
      if (field === 'isStatusUpdateEnabled') setIsStatusUpdateEnabled(value);
      if (field === 'isStatusUpdateOnTourStopsEnabled') setIsStatusUpdateOnTourStopsEnabled(value);
      if (field === 'isStatusUpdateForDispatchersEnabled') setIsStatusUpdateForDispatchersEnabled(value);
      App.showPopupNotification('Setting saved.', 'success');
      setIsSavingSettings(false);
    }, () => {
      App.showPopupNotification('Failed to update setting.', 'error');
      setIsSavingSettings(false);
    });
  }, [isSavingSettings, isStatusUpdateEnabled, isStatusUpdateOnTourStopsEnabled, isStatusUpdateForDispatchersEnabled]);

  const historyRows: KstDataGridRows = useMemo(
    () => ({ entries: historyEntries }),
    [historyEntries]
  );

  const loadHistory = useCallback(() => {
    if (!historyFromDate || !historyToDate) {
      App.showPopupNotification('Please select both a start and end date.', 'warning');
      return;
    }
    setIsHistoryLoading(true);
    api.getStatusUpdateHistory(
      { from: historyFromDate.toISOString(), to: historyToDate.toISOString(), personId: historyOfficerFilter },
      (result: any) => {
        setHistoryEntries(result.statusUpdateHistory || []);
        setHistoryOfficerOptions((result.persons || []).map((p: any) => ({ id: p.id, name: p.name })));
        setIsHistoryLoading(false);
      },
      () => {
        App.showPopupNotification('Error loading status history.', 'error');
        setIsHistoryLoading(false);
      }
    );
  }, [historyFromDate, historyToDate, historyOfficerFilter]);

  // Auto-load history when the History tab is first shown
  useEffect(() => {
    if (activeTab === 'showHistoryTab' && !historyLoadedRef.current) {
      historyLoadedRef.current = true;
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  const exportHistoryCsv = useCallback(() => {
    const data = (historyEntries || []).map((entry: any) => ({
      [officerLabel]: entry.officerName || '',
      'Updated By': entry.updatedByName || '',
      'Status': entry.statusComment || 'Cleared',
      'Latitude': entry.latitude ?? '',
      'Longitude': entry.longitude ?? '',
      'Date': hasValue(entry.dateCreated) ? moment(entry.dateCreated).format('MM/DD/YYYY HH:mm:ss') : '',
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    saveAs(blob, 'Status Update History.csv');
  }, [historyEntries, officerLabel]);

  return (
    <div className={theme}>
      <AddStatusUpdateOptionModal
        open={addModalOpen}
        theme={theme}
        saving={isSaving}
        editItem={editItem}
        onSave={handleAddSave}
        onCancel={() => { setAddModalOpen(false); setEditItem(null); }}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Status Update Option"
        message={deleteConfirmMessage}
        confirmText="Delete"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showSettingsTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showSettingsTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Settings</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showHistoryTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showHistoryTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span className="kst-sidebar-text">History</span>
              </li>
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* Settings Tab */}
            {activeTab === 'showSettingsTab' && (
              <div className="kst-page" ref={gridContainerRef}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Status Management</h3>
                  <p className="kst-page-description">
                    Configure the status update feature for your company. Enable the feature and manage predefined status update options.
                  </p>
                </div>

                {settingsLoaded ? (
                  <div style={{ marginBottom: 24, opacity: isSavingSettings ? 0.5 : 1, pointerEvents: isSavingSettings ? 'none' : 'auto', transition: 'opacity 0.15s' }}>
                    <h4 style={{ margin: '0 0 8px 0' }}>Feature Settings</h4>
                    <p className="kst-page-description" style={{ margin: '0 0 12px 0' }}>{officerPluralLabel}</p>
                    <label className="kst-checkbox-group">
                      <input type="checkbox" className="kstc-checkbox" checked={isStatusUpdateEnabled} onChange={(e) => handleSettingChange('isStatusUpdateEnabled', e.target.checked)} />
                      <span className="kstc-checkbox-label">{`Enable status update button for ${officerPluralLabel.toLowerCase()}`}</span>
                    </label>
                    <label className="kst-checkbox-group">
                      <input type="checkbox" className="kstc-checkbox" checked={isStatusUpdateOnTourStopsEnabled} onChange={(e) => handleSettingChange('isStatusUpdateOnTourStopsEnabled', e.target.checked)} />
                      <span className="kstc-checkbox-label">Automatically send status updates when tour stops are started or completed</span>
                    </label>
                    <p className="kst-page-description" style={{ margin: '12px 0 12px 0' }}>Dispatchers</p>
                    <label className="kst-checkbox-group">
                      <input type="checkbox" className="kstc-checkbox" checked={isStatusUpdateForDispatchersEnabled} onChange={(e) => handleSettingChange('isStatusUpdateForDispatchersEnabled', e.target.checked)} />
                      <span className="kstc-checkbox-label">Enable status update button for dispatchers</span>
                    </label>
                  </div>
                ) : (
                  <div className="kst-loading" style={{ marginBottom: 24 }}>Loading settings...</div>
                )}

                <h4 style={{ margin: '0 0 8px 0' }}>Status Options</h4>
                <p className="kst-page-description" style={{ marginBottom: 8 }}>
                  {`Define the predefined statuses available in the dropdown. ${officerPluralLabel} can also type a custom status.`}
                </p>

                <KstDataGrid
                  columns={statusUpdateOptionColumns}
                  rows={rows}
                  headerbuttons={headerButtons}
                  showcheckbox={true}
                  showordercolumn={true}
                  showstatustoggle={true}
                  mobiletitlefield="name"
                  loading={isLoading}
                  onButtonCommand={handleButtonCommand}
                  onRowMoved={handleRowMoved}
                  onRowClick={handleRowClick}
                  onStatusSwitch={handleStatusSwitch}
                />
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'showHistoryTab' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Status History</h3>
                  <p className="kst-page-description">
                    {`View a log of all status updates submitted by ${officerPluralLabel.toLowerCase()} and dispatchers. The "Updated By" column shows who made the update when it was done on behalf of another user.`}
                  </p>
                </div>

                <div className="kst-form-row" style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                  <div className="kstc-form-group" style={{ flex: '0 0 auto' }}>
                    <label className="kstc-label">From</label>
                    <DateTimePicker
                      value={historyFromDate}
                      type="datetime"
                      format="MM/DD/YYYY HH:mm"
                      valueType="date"
                      clearable={false}
                      appendToBody={true}
                      popupClass={theme}
                      onChange={setHistoryFromDate}
                    />
                  </div>
                  <div className="kstc-form-group" style={{ flex: '0 0 auto' }}>
                    <label className="kstc-label">To</label>
                    <DateTimePicker
                      value={historyToDate}
                      type="datetime"
                      format="MM/DD/YYYY HH:mm"
                      valueType="date"
                      clearable={false}
                      appendToBody={true}
                      popupClass={theme}
                      onChange={setHistoryToDate}
                    />
                  </div>
                  <div className="kstc-form-group" style={{ flex: '0 0 auto', minWidth: 180 }}>
                    <label className="kstc-label">{officerLabel}</label>
                    <Dropdownlist
                      value={historyOfficerFilter}
                      items={historyOfficerOptions}
                      labelKey="name"
                      valueKey="id"
                        placeholder={`All ${officerPluralLabel}`}
                        searchable={true}
                        clearable={true}
                        sortItems={false}
                        onChange={(val: any) => setHistoryOfficerFilter(val)}
                      />
                    </div>
                  <button
                    type="button"
                    className="btn-search"
                    disabled={isHistoryLoading}
                    onClick={loadHistory}
                  >
                    {isHistoryLoading ? 'Loading...' : 'Search'}
                  </button>
                </div>

                <div className="kst-results-panel">
                  <div className="kst-results-header">
                    <span className="kst-results-header-title">Status History</span>
                    <button
                      type="button"
                      className="kst-download-btn"
                      disabled={!historyEntries || historyEntries.length === 0}
                      onClick={exportHistoryCsv}
                      title="Download CSV"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  </div>
                  <div className="kst-results-body">
                    <KstDataGrid
                      columns={historyColumns}
                      rows={historyRows}
                      showcheckbox={false}
                      showheader={true}
                      mobiletitlefield="officerName"
                      loading={isHistoryLoading}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatusUpdateOptionManagementScreen;
